import { Avatar, Badge, Box, Button, Card, Group, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { IconLogout } from '@tabler/icons-react'
import React from 'react'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'

function initials(value: string): string {
  const v = value.trim()
  if (!v) return '?'
  return v.slice(0, 2).toUpperCase()
}

/** Account info page — user identity, plan/entitlements, workspaces, and sign-out. */
export const AccountPage: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const user = auth.user
  const display = user?.email || user?.id || 'Account'
  const activeWorkspace = auth.workspaces.find((w) => w.id === auth.workspaceId) ?? null

  return (
    <Box style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
        <Stack gap="md" p="lg" maw={760} mx="auto" w="100%">
          <Title order={3}>Account</Title>

          <Card withBorder radius="md" padding="lg">
            <Group gap="md" wrap="nowrap">
              <Avatar radius="xl" size={56} color="grape">
                {initials(display)}
              </Avatar>
              <Box style={{ minWidth: 0 }}>
                <Text fw={600} size="lg" lineClamp={1}>
                  {user?.email || 'Signed in'}
                </Text>
                <Group gap="xs" mt={4}>
                  <Badge variant="light" color={auth.isProMode ? 'grape' : 'gray'}>
                    {auth.isProMode ? 'Pro' : 'Free'}
                  </Badge>
                  {user?.isAdmin ? <Badge variant="light" color="blue">Admin</Badge> : null}
                </Group>
              </Box>
            </Group>
          </Card>

          <Card withBorder radius="md" padding="lg">
            <Stack gap="sm">
              <Text fw={600}>Details</Text>
              <Group justify="space-between" gap="sm" wrap="nowrap">
                <Text size="sm" c="dimmed">Email</Text>
                <Text size="sm" style={{ wordBreak: 'break-all' }}>{user?.email || '—'}</Text>
              </Group>
              <Group justify="space-between" gap="sm" wrap="nowrap">
                <Text size="sm" c="dimmed">User ID</Text>
                <Text size="sm" style={{ wordBreak: 'break-all' }}>{user?.id || '—'}</Text>
              </Group>
              <Group justify="space-between" gap="sm" wrap="nowrap">
                <Text size="sm" c="dimmed">Active workspace</Text>
                <Text size="sm">{activeWorkspace?.name || '—'}</Text>
              </Group>
              <Group justify="space-between" gap="sm" wrap="nowrap">
                <Text size="sm" c="dimmed">Workspaces</Text>
                <Text size="sm">{auth.workspaces.length}</Text>
              </Group>
            </Stack>
          </Card>

          {auth.entitlement && auth.entitlement.featureDetails.length > 0 ? (
            <Card withBorder radius="md" padding="lg">
              <Stack gap="sm">
                <Text fw={600}>Plan features</Text>
                <Group gap="xs">
                  {auth.entitlement.featureDetails.map((f) => (
                    <Badge key={f.license_id} variant="light">
                      {f.friendly_name}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Card>
          ) : null}

          <Group>
            <Button
              color="red"
              variant="light"
              leftSection={<IconLogout size={16} />}
              onClick={() => void auth.logout()}
            >
              Sign out
            </Button>
          </Group>
        </Stack>
      </ScrollArea>
    </Box>
  )
}
